import React, { useState, useEffect } from 'react';
import './RestaurantInfo.css';
import { getRestaurantInfo, saveRestaurantInfo } from '../../../utils/restaurantInfo';
import { deleteRestaurantLogo } from '../../../utils/storage';
import ConfirmModal from '../../common/ConfirmModal';
import SuccessToast from '../../common/SuccessToast';

const RestaurantInfo = ({ onSave, onChanges }) => {
  const [formData, setFormData] = useState({
    logo: null,
    name: '',
    phone: '',
    email: '',
    address: '',
    description: ''
  });
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);
  const [showSuccessToast, setShowSuccessToast] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  // Load existing restaurant info on component mount
  useEffect(() => {
    loadRestaurantInfo();
  }, []);

  const loadRestaurantInfo = async () => {
    try {
      setFetching(true);
      const { data, error } = await getRestaurantInfo();
      
      if (error) {
        console.error('Error loading restaurant info:', error);
        // Don't show error to user, just use empty form
        setFetching(false);
        return;
      }

      if (data) {
        setFormData({
          logo: data.logo_url || null,
          name: data.name || '',
          phone: data.phone || '',
          email: data.email || '',
          address: data.address || '',
          description: data.description || ''
        });
        setLogoPreview(data.logo_url || null);
      }
    } catch (error) {
      console.error('Error loading restaurant info:', error);
    } finally {
      setFetching(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    onChanges();
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        alert('Please select an image file');
        return;
      }

      // Validate file size (2MB max)
      if (file.size > 2 * 1024 * 1024) {
        alert('Logo file is too large. Maximum size is 2MB');
        return;
      }

      setLogoFile(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result);
      };
      reader.readAsDataURL(file);
      
      onChanges();
    }
  };

  const handleRemoveLogo = () => {
    setShowRemoveConfirm(true);
  };

  const confirmRemoveLogo = async () => {
    setShowRemoveConfirm(false);
    setLoading(true);
    
    try {
      // If there's an existing logo URL, delete it from storage
      if (formData.logo && formData.logo.startsWith('http')) {
        try {
          await deleteRestaurantLogo(formData.logo);
        } catch (error) {
          console.warn('Could not delete logo from storage:', error);
          // Continue even if deletion fails
        }
      }
      
      // Update form data to remove logo
      const updatedFormData = { ...formData, logo: null };
      setFormData(updatedFormData);
      setLogoPreview(null);
      setLogoFile(null);
      
      // Save to database
      const { data, error } = await saveRestaurantInfo(updatedFormData, null);
      
      if (error) {
        throw error;
      }
      
      // Show success message
      setSuccessMessage('Logo removed successfully!');
      setShowSuccessToast(true);
      onSave();
      onChanges();
    } catch (error) {
      console.error('Error removing logo:', error);
      setSuccessMessage('Failed to remove logo. Please try again.');
      setShowSuccessToast(true);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    // Validate required fields before saving
    if (!formData.name || !formData.name.trim()) {
      alert('Please enter restaurant name');
      return;
    }
    if (!formData.phone || !formData.phone.trim()) {
      alert('Please enter phone number');
      return;
    }
    if (!formData.email || !formData.email.trim()) {
      alert('Please enter email address');
      return;
    }
    if (!formData.address || !formData.address.trim()) {
      alert('Please enter restaurant address');
      return;
    }

    try {
      setLoading(true);
      console.log('Saving restaurant information to Supabase...');
      
      const { data, error } = await saveRestaurantInfo(formData, logoFile);
      
      if (error) {
        console.error('❌ Error saving restaurant info:', error);
        const errorMessage = error.message || 'Failed to save restaurant information. Please try again.';
        alert(`Error: ${errorMessage}`);
        return;
      }

      if (!data) {
        console.error('❌ No data returned from save operation');
        alert('Failed to save restaurant information. No data returned.');
        return;
      }

      // Update form data with saved data
      setFormData(prev => ({
        ...prev,
        logo: data.logo_url
      }));
      setLogoPreview(data.logo_url);
      setLogoFile(null); // Clear file after successful upload

      console.log('✅ Restaurant information saved successfully to Supabase!');
    onSave();
    } catch (error) {
      console.error('❌ Unexpected error saving restaurant info:', error);
      alert(`Failed to save restaurant information: ${error.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  if (fetching) {
    return (
      <div className="settings-section">
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <p>Loading restaurant information...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="settings-section">
      <div className="section-header">
        <h2 className="section-title">Restaurant Information</h2>
        <p className="section-subtitle">Basic details about your restaurant</p>
        <div className="section-divider"></div>
      </div>

      <div className="form-content">
        <div className="form-group">
          <label className="form-label">Restaurant Logo</label>
          <div className="logo-upload-section">
            <div className="logo-preview">
              {logoPreview ? (
                <img src={logoPreview} alt="Restaurant logo" />
              ) : (
                <div className="logo-placeholder">🏪</div>
              )}
            </div>
            <div className="logo-buttons-container">
              <label className="change-logo-btn">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleLogoChange}
                  style={{ display: 'none' }}
                  disabled={loading}
                />
                {loading ? 'Uploading...' : 'Change Logo'}
              </label>
              {logoPreview && (
                <button
                  type="button"
                  onClick={handleRemoveLogo}
                  className="remove-logo-btn"
                  disabled={loading}
                >
                  Remove Logo
                </button>
              )}
            </div>
            <div className="helper-text">Recommended size: 512x512px, Max 2MB</div>
          </div>
        </div>

        <div className="form-group">
          <label className="form-label">
            Restaurant Name <span className="required">*</span>
          </label>
          <input
            type="text"
            name="name"
            value={formData.name}
            onChange={handleChange}
            className="form-input"
            placeholder="Enter restaurant name"
          />
        </div>

        <div className="form-group">
          <label className="form-label">
            Phone Number <span className="required">*</span>
          </label>
          <div className="input-with-icon">
            <span className="input-icon">📞</span>
            <input
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              className="form-input"
              placeholder="Enter phone number"
            />
          </div>
        </div>

        <div className="form-group">
          <label className="form-label">
            Email Address <span className="required">*</span>
          </label>
          <div className="input-with-icon">
            <span className="input-icon">✉️</span>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className="form-input"
              placeholder="Enter email address"
            />
          </div>
        </div>

        <div className="form-group">
          <label className="form-label">
            Restaurant Address <span className="required">*</span>
          </label>
          <textarea
            name="address"
            value={formData.address}
            onChange={handleChange}
            className="form-textarea"
            rows="4"
            placeholder="Enter complete address with landmarks"
          />
        </div>

        <div className="form-group">
          <label className="form-label">Restaurant Description</label>
          <textarea
            name="description"
            value={formData.description}
            onChange={handleChange}
            className="form-textarea"
            rows="5"
            placeholder="Tell customers about your restaurant..."
            maxLength={500}
          />
          <div className="char-counter">{formData.description.length}/500</div>
        </div>

        <div className="form-actions">
          <button 
            className="save-btn" 
            onClick={handleSave}
            disabled={loading}
          >
            {loading ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>

      <ConfirmModal
        isOpen={showRemoveConfirm}
        onClose={() => setShowRemoveConfirm(false)}
        onConfirm={confirmRemoveLogo}
        title="Remove Logo"
        message="Are you sure you want to remove the restaurant logo? This action cannot be undone."
        confirmText="Remove Logo"
        cancelText="Cancel"
        type="danger"
      />

      {showSuccessToast && (
        <SuccessToast
          message={successMessage}
          onClose={() => setShowSuccessToast(false)}
          duration={3000}
          type={successMessage.includes('Failed') ? 'error' : 'success'}
        />
      )}
    </div>
  );
};

export default RestaurantInfo;

